<template>
  <v-hover v-slot="{ isHovering, props }">
    <v-avatar
      v-bind="mergeProps($attrs, props)"
      class="avatar-view"
      :color="avatarColor"
      :border="avatarBorder"
      :size="size"
    >
      <v-img
        v-if="avatarUrl"
        :src="avatarUrl"
        class="profile-image"
      />
      <span
        v-else-if="truncatedName"
        :class="fontClass"
        v-text="truncatedName"
      />
      <v-overlay
        :model-value="isEditable && (isHovering || showWhenEditable)"
        contained
        persistent
        :close-on-content-click="false"
        class="avatar--overlay"
      >
        <v-icon
          id="avatar--edit"
          color="white"
          icon="mdi-pencil"
          @click="editProfilePhoto"
        />
        <input
          ref="fileOpenButton"
          type="file"
          style="display: none;"
          @change="onFileChange"
        >
      </v-overlay>
    </v-avatar>
  </v-hover>
</template>

<script lang="ts">
import { computed, defineComponent, mergeProps, ref } from 'vue'

import type { PropType } from 'vue'

export default defineComponent({
  name: 'Avatar',

  inheritAttrs: false,

  emits: {
    fileChange: (_: Event) => true,
  },

  props: {
    first: { type: String, default: '' },
    last: { type: String, default: '' },
    avatarUrl: { type: String as PropType<string | null>, default: null },
    seed: { type: Number as PropType<number | null>, default: null },
    border: { type: String, default: '' },
    size: { type: Number, default: 44 },
    fontClass: { type: String, default: '' },
    isEditable: { type: Boolean, default: false },
    showWhenEditable: { type: Boolean, default: false },
  },

  setup (props, { emit }) {
    const avatarBorder = computed(() => {
      if (props.border) return props.border
      if (props.seed) return 'sm dashed'
      return undefined
    })
    const avatarColor = computed(() => {
      if (props.seed === null && props.avatarUrl === null) return 'transparent'
      // color selection for avatar backgrounds
      const colors = [
        '#196A9C',
        '#147AB9',
        '#61308A',
        '#9B1D63',
        '#D3135E',
        '#EC5D34',
        '#F2A547',
      ]
      return props.avatarUrl
        ? 'white'
        : colors[props.seed! % colors.length]!
    })
    const truncatedName = computed(() => {
      return props.first.substring(0, 1).toUpperCase() + props.last.substring(0, 1).toUpperCase()
    })

    const fileOpenButton = ref<HTMLInputElement | null>(null)
    function editProfilePhoto (): void {
      fileOpenButton.value?.click()
    }
    function onFileChange (e: Event): void {
      emit('fileChange', e)
    }

    return {
      mergeProps,

      avatarBorder,
      avatarColor,
      truncatedName,

      fileOpenButton,
      editProfilePhoto,
      onFileChange,
    }
  },
})
</script>

<!-- eslint-disable-next-line vue/enforce-style-attribute -->
<style lang="sass">
.avatar-badge
  .avatar-view
    position: relative
    .profile-image
      width: 100%
      height: 100%
      border-radius: 50%
      background-repeat: no-repeat
      background-position: center
      background-size: cover
      position: relative
    &.account
      border-width: 4px
.avatar--overlay
  align-items: center
  justify-content: center
</style>
